import React, { useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { selectScreen, selectElement } from './utils/state/canvasSlice';
import { updateScreenUIElement } from './utils/state/screensSlice';
import ComprehensiveScreenEditor from './ComprehensiveScreenEditor';
import ComprehensiveUIElementEditor from './ComprehensiveUIElementEditor';
import ElementPalette from './ElementPalette';
import {
    ChevronRight,
    ChevronLeft,
    Settings,
    Square,
    Palette,
} from 'lucide-react';
import { Button } from '@/src/shadComponents/ui/button';

interface UnifiedRightPanelProps {
    screens: ScreenDefinition[];
}

export default function UnifiedRightPanel({ screens }: UnifiedRightPanelProps) {
    const dispatch = useDispatch();
    const [isCollapsed, setIsCollapsed] = useState(false);
    const [panelWidth, setPanelWidth] = useState(320);

    const { selectedScreenId, selectedElementId } = useSelector(
        (state: any) => state.canvas,
    );

    // Get current screen directly from Redux state to avoid prop timing issues
    const currentScreen: ScreenDefinition | null = useSelector((state: any) => {
        if (selectedScreenId) {
            return state.screens.present.entities[selectedScreenId] || null;
        }
        return null;
    });

    // Get selected element
    const selectedElement =
        selectedElementId && currentScreen
            ? currentScreen.elements.find(
                  (el: UIElement) => el.id === selectedElementId,
              )
            : null;

    const handleClearSelection = () => {
        dispatch(selectScreen(undefined));
        dispatch(selectElement(undefined));
    };

    const hasSelection = selectedScreenId || selectedElementId;

    // Determine what to show in the panel
    const showElementEditor = selectedElement && currentScreen;
    const showScreenEditor = currentScreen && !selectedElement;

    if (isCollapsed) {
        return (
            <div className="w-12 bg-background border-l border-border flex flex-col items-center justify-start pt-4">
                <Button
                    variant="ghost"
                    size="icon-sm"
                    onClick={() => setIsCollapsed(false)}
                    title="Expand Panel"
                >
                    <ChevronLeft className="w-4 h-4" />
                </Button>
                {hasSelection && (
                    <div className="mt-4 p-2 rounded-lg bg-blue-100">
                        {selectedElement ? (
                            <Square className="w-4 h-4 text-blue-600" />
                        ) : (
                            <Settings className="w-4 h-4 text-blue-600" />
                        )}
                    </div>
                )}
                {!hasSelection && (
                    <div className="mt-4 p-2 rounded-lg bg-purple-100">
                        <Palette className="w-4 h-4 text-purple-600" />
                    </div>
                )}
            </div>
        );
    }

    return (
        <div
            className="bg-background border-l border-border flex flex-col"
            style={{ width: panelWidth }}
        >
            {/* Panel Header */}
            <div className="flex items-center justify-between p-4 border-b border-border bg-muted/50">
                <div className="flex items-center gap-2">
                    {showElementEditor ? (
                        <>
                            <Square className="w-4 h-4 text-blue-600" />
                            <span className="text-sm font-semibold text-foreground">
                                Element Properties
                            </span>
                        </>
                    ) : showScreenEditor ? (
                        <>
                            <Settings className="w-4 h-4 text-green-600" />
                            <span className="text-sm font-semibold text-foreground">
                                Screen Properties
                            </span>
                        </>
                    ) : (
                        <>
                            <Palette className="w-4 h-4 text-purple-600" />
                            <span className="text-sm font-semibold text-foreground">
                                Element Palette
                            </span>
                        </>
                    )}
                </div>

                <div className="flex items-center gap-1">
                    {hasSelection && (
                        <Button
                            variant="ghost"
                            size="icon-xs"
                            onClick={handleClearSelection}
                            title="Back to Element Palette"
                        >
                            <Palette className="w-4 h-4" />
                        </Button>
                    )}
                    <Button
                        variant="ghost"
                        size="icon-xs"
                        onClick={() => setIsCollapsed(true)}
                        title="Collapse Panel"
                    >
                        <ChevronRight className="w-4 h-4" />
                    </Button>
                </div>
            </div>

            {/* Panel Content */}
            <div className="flex-1 overflow-y-auto pb-4">
                {showElementEditor ? (
                    <div className="p-4">
                        <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                            <div className="text-sm font-medium text-blue-900">
                                {selectedElement.id}
                            </div>
                            <div className="text-xs text-blue-700 mt-1">
                                {selectedElement.type} • Layer{' '}
                                {selectedElement.layer || 0}
                            </div>
                            <div className="text-xs text-blue-600 mt-1">
                                {selectedElement.horizontalLocation} ×{' '}
                                {selectedElement.verticalLocation}
                            </div>
                        </div>
                        <ComprehensiveUIElementEditor
                            element={selectedElement}
                            screen={currentScreen}
                            onIdChange={(newId: string) => {
                                if (selectedElement && currentScreen) {
                                    dispatch(
                                        updateScreenUIElement({
                                            screenId: currentScreen.id,
                                            elementId: selectedElement.id,
                                            key: 'id',
                                            value: newId,
                                        }),
                                    );
                                    // Also update the metadata ID to keep them in sync
                                    dispatch(
                                        updateScreenUIElement({
                                            screenId: currentScreen.id,
                                            elementId: selectedElement.id,
                                            key: 'metadata',
                                            value: {
                                                ...selectedElement.metadata,
                                                id: newId,
                                            },
                                        }),
                                    );
                                }
                            }}
                        />
                    </div>
                ) : showScreenEditor ? (
                    <div className="p-4">
                        <div className="mb-4 p-3 bg-green-50 rounded-lg border border-green-200">
                            <div className="text-sm font-medium text-green-900">
                                {currentScreen.id}
                            </div>
                            <div className="text-xs text-green-700 mt-1">
                                {currentScreen.stage.type} stage •{' '}
                                {currentScreen.elements.length} elements
                            </div>
                        </div>
                        <ComprehensiveScreenEditor
                            screen={currentScreen}
                            onIdChange={(newId: string) => {
                                // The ComprehensiveScreenEditor now handles the debounced updates internally
                                // This callback is still needed for parent component awareness
                            }}
                        />
                    </div>
                ) : (
                    // Show Element Palette when nothing is selected
                    <div className="h-full">
                        <ElementPalette
                            isOpen={true}
                            onClose={() => {}} // No close button needed since it's integrated
                            rightOffset={0} // No offset needed since it's integrated
                            embedded={true} // New prop to indicate embedded mode
                        />
                    </div>
                )}
            </div>

            {/* Resize Handle */}
            <div
                className="absolute top-0 left-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-500 transition-colors"
                onMouseDown={(e) => {
                    const startX = e.clientX;
                    const startWidth = panelWidth;

                    const handleMouseMove = (e: MouseEvent) => {
                        const newWidth = Math.max(
                            280,
                            Math.min(600, startWidth - (e.clientX - startX)),
                        );
                        setPanelWidth(newWidth);
                    };

                    const handleMouseUp = () => {
                        document.removeEventListener(
                            'mousemove',
                            handleMouseMove,
                        );
                        document.removeEventListener('mouseup', handleMouseUp);
                    };

                    document.addEventListener('mousemove', handleMouseMove);
                    document.addEventListener('mouseup', handleMouseUp);
                }}
            />
        </div>
    );
}
